import React from "react";

import { Panel } from "@/components/ui";
import { FieldSet, FormField, TextField } from "@/components/ui/Form";

export interface InitialApprovalDetails {
  institutionalLabel: string;
  effectiveFrom: string;
  nextRevalidationDue: string;
}

/** What an approved revision's effective date means, shown wherever one is set. */
export const EFFECTIVE_DATE_HELP =
  "The date this approved revision becomes current. Any earlier revision is superseded from that day.";

export function InitialApprovalFields({
  idPrefix,
  value,
  onChange,
  disabled = false,
  disabledReason = undefined,
}: {
  idPrefix: string;
  value: InitialApprovalDetails;
  onChange: (value: InitialApprovalDetails) => void;
  disabled?: boolean;
  /** Why every control here refuses, when it does. */
  disabledReason?: string;
}): React.ReactElement {
  const update = (field: keyof InitialApprovalDetails, fieldValue: string): void => {
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <Panel className="mt-3">
      <FieldSet
        legend="Approval details"
        description="These details identify the first approved curriculum revision and when it applies."
        columns={2}
        data-testid={`${idPrefix}-approval-details`}
      >
        <FormField label="Institutional revision label" htmlFor={`${idPrefix}-approval-label`}>
          <TextField
            id={`${idPrefix}-approval-label`}
            value={value.institutionalLabel}
            onChange={(next) => update("institutionalLabel", next)}
            placeholder="For example, Initial Validation 2026"
            disabled={disabled}
            disabledReason={disabledReason}
            required
            data-testid={`${idPrefix}-approval-label`}
          />
        </FormField>
        <FormField
          label="Effective from"
          htmlFor={`${idPrefix}-approval-effective-from`}
          helpText={EFFECTIVE_DATE_HELP}
        >
          <TextField
            id={`${idPrefix}-approval-effective-from`}
            type="date"
            value={value.effectiveFrom}
            onChange={(next) => update("effectiveFrom", next)}
            disabled={disabled}
            disabledReason={disabledReason}
            required
            data-testid={`${idPrefix}-approval-effective-from`}
          />
        </FormField>
        <FormField
          label="Next revalidation due (optional)"
          htmlFor={`${idPrefix}-approval-next-revalidation`}
        >
          <TextField
            id={`${idPrefix}-approval-next-revalidation`}
            type="date"
            value={value.nextRevalidationDue}
            onChange={(next) => update("nextRevalidationDue", next)}
            disabled={disabled}
            disabledReason={disabledReason}
            data-testid={`${idPrefix}-approval-next-revalidation`}
          />
        </FormField>
      </FieldSet>
    </Panel>
  );
}

export function validateInitialApprovalDetails(value: InitialApprovalDetails): string | undefined {
  if (!value.institutionalLabel.trim() || !value.effectiveFrom) {
    return "Add an institutional revision label and effective date before approval.";
  }
  if (value.nextRevalidationDue && value.nextRevalidationDue < value.effectiveFrom) {
    return "The next revalidation date cannot be before the effective date.";
  }
  return undefined;
}
